import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Self,
    viewChild,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {escapeRegExp} from 'lodash-es';
import {NumberPipe} from '../pipes/number.pipe';
import {AppValidators, TranslateService} from '@brunata-angular/util';
import {ControlMessagesComponent} from '../control-messages/control-messages.component';
import {v4 as uuidv4} from 'uuid';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {patchControlMethod} from '../patch-control-method';

@Component({
    selector: 'app-input-number-group',
    templateUrl: './input-number-group.component.html',
    styleUrls: ['./input-number-group.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        ControlMessagesComponent,
        PopoverIconComponent,
    ],
    providers: [NumberPipe],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputNumberGroupComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private readonly htmlInputElement =
        viewChild.required<ElementRef<HTMLInputElement>>('htmlInputElement');

    @Input() label = '';
    @Input() descriptor: string | undefined;
    @Input() information: string | undefined;
    @Input() minDigits: number | undefined;
    @Input() maxDigits: number | undefined;
    @Input() state?: 'updated' | 'error';
    @Input() name = '';
    @Input() autocomplete = 'on';
    @Input() selectOnFocus = false;
    @Input() maxlength: number | undefined;
    @Input() id: string = uuidv4();
    /**
     * When set, renders the Bootstrap grid layout (label in a `col-{labelCols}`, field in the rest)
     * instead of the default stacked layout. ngwebbill passes a value (0 = no label / full-width
     * input, >0 = offset grid) to match its legacy app-local component; other apps leave it unset.
     */
    @Input() labelCols: number | null = null;
    /**
     * Explicit required flag. When provided it wins over the validator-derived value, so ngwebbill
     * can drive the asterisk via `[required]` like its legacy component. When left undefined the
     * value is derived from a `Validators.required` on the control (other apps' behaviour).
     */
    @Input() required: boolean | undefined;

    @Output() focusEvent = new EventEmitter<string>();

    private readonly destroyRef = inject(DestroyRef);

    private onChange: ((value: number | null) => void) | undefined;
    private onTouch: (() => void) | undefined;

    private restoreMarkAsFunctions: (() => void) | undefined;

    /**
     * `FormControl.disable()` / `.enable()` emit `valueChanges`, and Angular forwards a parent
     * `disable()` here without its options, so a caller asking for silence gets one too. Those
     * emissions are not user edits and must not be written back to the parent control.
     */
    private applyingDisabledState = false;

    private readonly decimalPart: string;
    private readonly groupPart: string;
    private readonly minusSignPart: string;

    formControl: FormControl<string | null>;

    constructor(
        @Self() public ngControl: NgControl,
        private numberPipe: NumberPipe,
        private translateService: TranslateService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
        this.formControl = new FormControl('', {validators: this.numberCheck, nonNullable: true});
        // Find decimal, group and minus-sign strings from locale. Some locales (e.g. Norwegian)
        // use the Unicode minus sign U+2212 rather than the ASCII hyphen-minus.
        const formatter = new Intl.NumberFormat(this.translateService.language);
        const parts = formatter.formatToParts(-10000.1);
        this.decimalPart = parts.find(part => part.type === 'decimal')?.value ?? '';
        this.groupPart = parts.find(part => part.type === 'group')?.value ?? '';
        this.minusSignPart = parts.find(part => part.type === 'minusSign')?.value ?? '-';
    }

    get labelClass(): string {
        const required = this.required === true ? 'required' : '';
        // Grid layout adds the Bootstrap column class; the stacked layout uses just `required`.
        return this.labelCols != null ? `col-${this.labelCols} ${required}`.trim() : required;
    }

    get inputColsClass(): string {
        return this.labelCols != null && this.labelCols > 0 ? `col-${12 - this.labelCols}` : '';
    }

    ngOnInit(): void {
        this.ngControl.control?.addValidators([this.boundValidate]);
        this.formControl.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(status => {
                if (status === 'PENDING') {
                    this.ngControl.control?.markAsPending();
                }
            });

        // Explicit [required] wins; otherwise derive from the control's required validator.
        this.required =
            this.required ?? this.ngControl.control?.hasValidator(Validators.required) ?? false;

        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        const control = this.ngControl.control;
        if (control != null) {
            const markForCheck = (): void => this.changeDetectorRef.markForCheck();
            const restoreTouched = patchControlMethod(control, 'markAsTouched', markForCheck);
            const restoreUntouched = patchControlMethod(control, 'markAsUntouched', markForCheck);
            this.restoreMarkAsFunctions = (): void => {
                restoreTouched();
                restoreUntouched();
            };
        }

        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(valueFromInput => {
                this.changeDetectorRef.markForCheck();
                if (this.formControl.valid && valueFromInput != null && valueFromInput.length > 0) {
                    const numberValue = this.convertStringToNumber(valueFromInput);

                    // Format number and reposition cursor
                    let stringValue = this.numberPipe.transform(numberValue ?? 0);

                    // Allow a decimal part and trailing zeroes at the end of the value while typing. E.g. '123.' and '123.0'
                    const decimalPartRegExp = new RegExp(
                        `${escapeRegExp(this.decimalPart)}[0-9]*$`,
                        'g',
                    );
                    const decimalPartFromInput = valueFromInput.match(decimalPartRegExp);
                    if (decimalPartFromInput != null) {
                        stringValue = stringValue.replace(decimalPartRegExp, '');
                        const digits = this.maxDigits ?? this.minDigits;
                        if (digits != null) {
                            stringValue += decimalPartFromInput[0].slice(0, digits + 1);
                        } else {
                            stringValue += decimalPartFromInput[0];
                        }
                    }

                    // Find out where to put the cursor
                    const element = this.htmlInputElement().nativeElement;
                    const selectionStart = element.selectionStart ?? 0;

                    const groupPartsBeforeCursor = (
                        valueFromInput
                            .slice(0, selectionStart)
                            .match(new RegExp(escapeRegExp(this.groupPart), 'g')) || []
                    ).length;
                    let newSelectionStart = 0;
                    let nonGroupPartCounter = 0;
                    while (nonGroupPartCounter < selectionStart - groupPartsBeforeCursor) {
                        if (stringValue.charAt(newSelectionStart) !== this.groupPart) {
                            nonGroupPartCounter++;
                        }
                        newSelectionStart++;
                    }

                    // Update input with formatted value
                    this.formControl.setValue(stringValue, {
                        emitEvent: false,
                    });
                    element.selectionStart = newSelectionStart;
                    element.selectionEnd = newSelectionStart;
                }

                // Notify the parent of every edit, not just valid ones: Enter submits without a
                // blur, so a cleared or non-numeric value has to reach the control here or the form
                // keeps handing over the value it was loaded with.
                if (!this.applyingDisabledState) {
                    this.notifyValueChange();
                }
            });
    }

    /** Angular's `cleanUpControl` does not remove the validator added above. (AGI-19494) */
    ngOnDestroy(): void {
        this.restoreMarkAsFunctions?.();

        const control = this.ngControl.control;
        if (control == null) {
            return;
        }
        control.removeValidators(this.boundValidate);
        control.updateValueAndValidity({emitEvent: false});
    }

    onFocus(event: FocusEvent): void {
        if (this.selectOnFocus) {
            const target = event.target as HTMLInputElement;
            target.select();
        }
        this.focusEvent.emit(this.name);
    }

    onBlur(): void {
        if (this.onTouch != null) {
            this.onTouch();
        }

        this.changeDetectorRef.markForCheck();
        // Set decimals
        if (this.formControl.valid && this.formControl.value != null) {
            const numberValue = this.convertStringToNumber(this.formControl.value);
            const pipedValue = this.numberPipe.transform(
                numberValue ?? 0,
                this.minDigits,
                this.maxDigits,
            );
            const stringValue = numberValue != null ? pipedValue : this.formControl.value;
            this.formControl.setValue(stringValue, {
                emitEvent: false,
            });
        }
        this.notifyValueChange();
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: number | null): void {
        if (value === null) {
            this.formControl.reset(null, {emitEvent: false});
            return;
        }

        const newValue = this.numberPipe.transform(value, this.minDigits, this.maxDigits);
        this.formControl.setValue(newValue);
    }

    setDisabledState(isDisabled: boolean): void {
        this.applyingDisabledState = true;
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
        this.applyingDisabledState = false;
    }

    onKeyDown(event: KeyboardEvent): void {
        // Replace keypress of group part with decimal part
        if (event.key === this.groupPart) {
            const target = event.target as HTMLInputElement;
            const start = target.selectionStart ?? 0;
            const end = target.selectionEnd ?? 0;
            const oldValue = target.value;
            const newValue = oldValue.slice(0, start) + this.decimalPart + oldValue.slice(end);
            this.formControl.setValue(newValue);
            target.selectionStart = target.selectionEnd = start + 1;
            event.preventDefault();
        }
    }

    private validate(_: AbstractControl): ValidationErrors | null {
        return this.formControl.errors;
    }

    /** One stable reference: `removeValidators` matches by identity. */
    private readonly boundValidate = (control: AbstractControl): ValidationErrors | null =>
        this.validate(control);

    private convertFormattedStringToParsableString(formattedString: string): string {
        return formattedString
            .replaceAll(this.groupPart, '')
            .replaceAll(this.decimalPart, '.')
            .replaceAll(this.minusSignPart, '-');
    }
    private numberCheck = (control: AbstractControl): ValidationErrors | null => {
        if (AppValidators.isEmptyInputValue(control.value)) {
            return null; // don't validate empty values to allow optional controls
        }
        const parsableString = this.convertFormattedStringToParsableString(String(control.value));
        const regex = /^-?\d+(\.\d*)?$/;
        return regex.test(parsableString) && !isNaN(Number(parsableString))
            ? null
            : {numberCheck: 'A number is required'};
    };

    /**
     * The inner control spells "empty" two ways: null, after `writeValue(null)` resets it, and ''
     * once the user has cleared the box.
     */
    private notifyValueChange(): void {
        if (this.onChange == null) {
            return;
        }
        const value = this.formControl.value ?? '';
        if (AppValidators.isEmptyInputValue(value)) {
            this.onChange(null);
        } else {
            this.onChange(this.convertStringToNumber(value) ?? NaN);
        }
    }

    private convertStringToNumber(value: string): number | null {
        const parsableString = this.convertFormattedStringToParsableString(value);
        return parsableString.length > 0 ? parseFloat(parsableString) : null;
    }
}
